"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Image from "next/image";
import { Loader2, Paperclip } from "lucide-react";
import { toast } from "sonner";
import { Button } from "~/components/ui/button";
import { Input } from "~/components/ui/input";
import {
  getTrishIntroMessage,
  buildTrishHirePrompt,
  formatTrishRoleRecommendationsMessage,
  parseWebsiteFromInput,
  promptStarterForJobTitle,
  recommendTrishAgentRoles,
  resolveRoleLabelFromJobTitle,
  trishAgentProfilePrompt,
  trishAgentProfileSuggestions,
  trishBuildingMessage,
  trishChannelsPrompt,
  trishConfigChoicePrompt,
  trishGoalsPrompt,
  trishSignUpMessage,
  TRISH_CHANNEL_OPTIONS,
  TRISH_GOAL_OPTIONS,
  type TrishConfigPreference,
  type TrishOnboardingStep,
  type TrishProductName,
  type TrishRoleRecommendation,
} from "~/lib/marketplace/home-trish-onboarding";
import {
  HIRE_ONBOARDING_INITIAL_PROMPT_KEY,
  normalizeHireWebsiteSessionKey,
  readHireTrishOnboardingSavedState,
  writeHireTrishOnboardingTranscript,
} from "~/lib/marketplace/hire-onboarding-session";
import {
  markHireBuilderOnboardingComplete,
  readSavedHireBuilderOnboardingState,
  writeSavedHireBuilderOnboardingState,
} from "~/lib/marketplace/hire-builder-onboarding";
import type { IndustryVertical } from "~/lib/utils/industry-profile";
import { cn } from "~/lib/utils";
import { getMarketingChrome } from "~/lib/siteglue/marketing-chrome";
import { resolveClientMarketingBrand } from "~/lib/marketing-brand";
import type { OnboardingSessionChannel } from "~/lib/org-onboarding/onboarding-channels";
import { serializeOnboardingMessages } from "~/lib/org-onboarding/session-messages";
import { trackOnboardingSession } from "~/lib/org-onboarding/track-onboarding-session";

const TRISH_AVATAR_SRC = "/marketing/trish-onboarding-agent.png";

type ChatMessage = {
  id: string;
  role: "trish" | "user";
  content: string;
};

export type TrishSuggestedProfile = {
  agentName: string;
  jobTitle: string;
  profileImageUrl: string;
};

export type TrishOnboardingChatProps = {
  bootstrapComplete: boolean;
  isAnalyzing: boolean;
  bootstrapError?: string | null;
  isSignedIn: boolean;
  /** Display name after website analysis (optional). */
  businessName?: string;
  vertical?: IndustryVertical | null;
  /** Industry-tailored goal chips from website scrape (falls back to TRISH_GOAL_OPTIONS). */
  goalOptions?: string[] | null;
  suggestedProfile?: TrishSuggestedProfile | null;
  onSubmitWebsite: (url: string) => void;
  onSetupAgent: () => void;
  /** Fired when unsigned user reaches the sign-up step (show Preview Sign Up). */
  onRequestSignUp?: () => void;
  /** Apply agent name / title / image chosen in chat. */
  onAgentProfileSelected?: (profile: {
    agentName: string;
    jobTitle: string;
    profileImageUrl: string;
  }) => void;
  /** Free Live Chat tier vs full recommended configuration from website analysis. */
  onConfigPreference?: (preference: TrishConfigPreference) => void;
  /** Partner / white-label consultant persona (defaults to Trish). */
  consultantPersona?: {
    name: string;
    avatarSrc: string;
    productName?: string;
    introMessage?: string;
  };
  /** When set, syncs transcript to org admin inbox for live monitoring. */
  onboardingSessionTracking?: {
    brandSlug: string;
    sessionKey: string;
    channel?: OnboardingSessionChannel;
  };
  className?: string;
};

function nextId(): string {
  return `trish-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
}

function thinkingDelayMs(): number {
  return 650 + Math.floor(Math.random() * 350);
}

function ConsultantAvatar({
  className,
  size = 36,
  src,
  alt,
}: {
  className?: string;
  size?: number;
  src: string;
  alt: string;
}) {
  return (
    <Image
      src={src}
      alt={alt}
      width={size}
      height={size}
      className={cn(
        "shrink-0 rounded-full object-cover ring-2 ring-white",
        className,
      )}
      style={{ width: size, height: size, minWidth: size, minHeight: size }}
      priority
    />
  );
}

function persistInitialPrompt(prompt: string) {
  try {
    sessionStorage.setItem(HIRE_ONBOARDING_INITIAL_PROMPT_KEY, prompt);
  } catch {
    /* ignore */
  }
}

export function TrishOnboardingChat({
  bootstrapComplete,
  isAnalyzing,
  bootstrapError,
  isSignedIn,
  businessName: businessNameProp,
  vertical,
  goalOptions,
  suggestedProfile,
  onSubmitWebsite,
  onSetupAgent,
  onRequestSignUp,
  onAgentProfileSelected,
  onConfigPreference,
  consultantPersona,
  onboardingSessionTracking,
  className,
}: TrishOnboardingChatProps) {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [step, setStep] = useState<TrishOnboardingStep>("website");
  const [draft, setDraft] = useState("");
  const [jobTitle, setJobTitle] = useState("");
  const [roleLabel, setRoleLabel] = useState("");
  const [useCaseDescription, setUseCaseDescription] = useState("");
  const [agentName, setAgentName] = useState("");
  const [profileImageUrl, setProfileImageUrl] = useState("");
  const [businessName, setBusinessName] = useState("");
  const [websiteUrl, setWebsiteUrl] = useState<string | null>(null);
  const [isThinking, setIsThinking] = useState(true);
  const [introLoaded, setIntroLoaded] = useState(false);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [configPreference, setConfigPreference] = useState<TrishConfigPreference | null>(null);
  const [selectedGoals, setSelectedGoals] = useState<string[]>([]);
  const [selectedChannels, setSelectedChannels] = useState<string[]>([]);
  const [roleRecommendations, setRoleRecommendations] = useState<TrishRoleRecommendation[]>([]);
  const setupStartedRef = useRef(false);
  const signUpRequestedRef = useRef(false);
  const awaitingBootstrapRef = useRef(false);
  const postAnalyzeStartedRef = useRef(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const messagesRef = useRef(messages);
  messagesRef.current = messages;

  const isBusy = isThinking || isAnalyzing || isUploadingImage;
  const resolvedBusinessName =
    businessNameProp?.trim() || businessName.trim() || "your business";

  const productName: TrishProductName = useMemo(() => {
    if (consultantPersona?.productName?.trim()) {
      const name = consultantPersona.productName.trim();
      return name === "SiteGlue" ? "SiteGlue" : "Inbound GPT";
    }
    const brand = resolveClientMarketingBrand(
      typeof window !== "undefined" ? new URLSearchParams(window.location.search) : null,
    );
    return brand === "siteglue" || getMarketingChrome().brandId === "siteglue"
      ? "SiteGlue"
      : "Inbound GPT";
  }, [consultantPersona?.productName]);

  const consultantName = consultantPersona?.name?.trim() || "Trish";
  const consultantAvatarSrc = consultantPersona?.avatarSrc?.trim() || TRISH_AVATAR_SRC;

  const resolvedGoalOptions = useMemo(() => {
    const fromWebsite = goalOptions?.map((g) => g.trim()).filter(Boolean) ?? [];
    return fromWebsite.length > 0 ? fromWebsite.slice(0, 8) : [...TRISH_GOAL_OPTIONS];
  }, [goalOptions]);

  const jobTitleOptions = useMemo(
    () => roleRecommendations.map((r) => r.title),
    [roleRecommendations],
  );

  const profileOptions = useMemo(
    () =>
      trishAgentProfileSuggestions({
        recommendedName: suggestedProfile?.agentName,
        recommendedImageUrl: suggestedProfile?.profileImageUrl,
        jobTitle: jobTitle || suggestedProfile?.jobTitle,
      }),
    [
      suggestedProfile?.agentName,
      suggestedProfile?.profileImageUrl,
      suggestedProfile?.jobTitle,
      jobTitle,
    ],
  );

  const scrollToBottom = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, step, isThinking, isAnalyzing, scrollToBottom]);

  const persistTranscript = useCallback(
    (nextMessages: ChatMessage[], nextStep?: TrishOnboardingStep) => {
      const key = normalizeHireWebsiteSessionKey(
        websiteUrl || businessNameProp || businessName || "pending",
      );
      if (!key) return;
      writeHireTrishOnboardingTranscript(key, nextMessages, {
        step: nextStep ?? step,
        jobTitle,
        roleLabel,
        useCaseDescription,
        agentName,
        profileImageUrl,
        businessName: resolvedBusinessName,
        websiteUrl,
        configPreference: configPreference ?? undefined,
        selectedGoals,
        selectedChannels,
      });
    },
    [
      agentName,
      businessName,
      businessNameProp,
      configPreference,
      jobTitle,
      profileImageUrl,
      resolvedBusinessName,
      roleLabel,
      selectedChannels,
      selectedGoals,
      step,
      useCaseDescription,
      websiteUrl,
    ],
  );

  // Persist transcript + step so Sign Up / email verify can resume mid-flow.
  useEffect(() => {
    if (messages.length === 0) return;
    persistTranscript(messages);
  }, [
    messages,
    step,
    jobTitle,
    roleLabel,
    agentName,
    profileImageUrl,
    websiteUrl,
    configPreference,
    selectedGoals,
    selectedChannels,
    persistTranscript,
  ]);

  useEffect(() => {
    if (!onboardingSessionTracking?.sessionKey || messages.length < 1) return;
    const timer = window.setTimeout(() => {
      void trackOnboardingSession({
        brandSlug: onboardingSessionTracking.brandSlug,
        sessionKey: onboardingSessionTracking.sessionKey,
        channel: onboardingSessionTracking.channel,
        websiteUrl,
        visitorName: businessNameProp?.trim() || businessName.trim() || null,
        metadata: {
          step,
          messageCount: messagesRef.current.length,
          messages: serializeOnboardingMessages(messagesRef.current),
          lastActivityAt: new Date().toISOString(),
        },
      });
    }, 2000);
    return () => window.clearTimeout(timer);
  }, [
    messages,
    step,
    websiteUrl,
    businessName,
    businessNameProp,
    onboardingSessionTracking,
  ]);

  useEffect(() => {
    if (introLoaded) return;
    setIntroLoaded(true);

    const savedOnboarding = readSavedHireBuilderOnboardingState();
    const pendingUrl = savedOnboarding?.submittedWebsite?.trim() || "";
    const saved = readHireTrishOnboardingSavedState(
      pendingUrl ? normalizeHireWebsiteSessionKey(pendingUrl) : undefined,
    );

    if (saved?.messages?.length) {
      setMessages(
        saved.messages.map((m) => ({
          id: m.id,
          role: m.role === "user" ? "user" : "trish",
          content: m.content,
        })),
      );
      if (saved.step) setStep(saved.step as TrishOnboardingStep);
      if (saved.jobTitle) setJobTitle(saved.jobTitle);
      if (saved.roleLabel) setRoleLabel(saved.roleLabel);
      if (saved.useCaseDescription) setUseCaseDescription(saved.useCaseDescription);
      if (saved.agentName) setAgentName(saved.agentName);
      if (saved.profileImageUrl) setProfileImageUrl(saved.profileImageUrl);
      if (saved.businessName) setBusinessName(saved.businessName);
      if (saved.websiteUrl) setWebsiteUrl(saved.websiteUrl);
      if (saved.configPreference) setConfigPreference(saved.configPreference);
      if (saved.selectedGoals?.length) setSelectedGoals(saved.selectedGoals);
      if (saved.selectedChannels?.length) setSelectedChannels(saved.selectedChannels);
      if (saved.step === "analyzing" || savedOnboarding?.step === "analyzing") {
        awaitingBootstrapRef.current = true;
      }
      if (saved.step === "sign_up" && !isSignedIn) {
        signUpRequestedRef.current = true;
        onRequestSignUp?.();
      }
      setIsThinking(false);
      return;
    }

    void (async () => {
      setIsThinking(true);
      await new Promise((resolve) => setTimeout(resolve, thinkingDelayMs()));
      setIsThinking(false);
      const brand = resolveClientMarketingBrand(
        typeof window !== "undefined"
          ? new URLSearchParams(window.location.search)
          : null,
      );
      const introProduct =
        consultantPersona?.productName?.trim() ||
        (brand === "siteglue" || getMarketingChrome().brandId === "siteglue"
          ? "SiteGlue"
          : "Inbound GPT");
      const introContent =
        consultantPersona?.introMessage?.trim() ||
        getTrishIntroMessage(introProduct as TrishProductName).replace(
          /\bTrish\b/g,
          consultantName,
        );
      setMessages([{ id: nextId(), role: "trish", content: introContent }]);
    })();
  }, [consultantName, consultantPersona, introLoaded, isSignedIn, onRequestSignUp]);

  // Rebuild role chips after resume (or when vertical arrives late from website analysis).
  useEffect(() => {
    if (
      step !== "job_title" &&
      step !== "agent_profile" &&
      step !== "config_choice" &&
      step !== "sign_up" &&
      step !== "complete"
    ) {
      return;
    }
    if (roleRecommendations.length > 0) return;
    if (selectedGoals.length === 0 && selectedChannels.length === 0) return;
    setRoleRecommendations(
      recommendTrishAgentRoles({
        vertical,
        recommendedJobTitle: jobTitle || suggestedProfile?.jobTitle,
        goals: selectedGoals,
        channels: selectedChannels,
        productName,
      }),
    );
  }, [
    jobTitle,
    productName,
    roleRecommendations.length,
    selectedChannels,
    selectedGoals,
    step,
    suggestedProfile?.jobTitle,
    vertical,
  ]);

  const appendUserMessage = useCallback((content: string) => {
    setMessages((prev) => [...prev, { id: nextId(), role: "user", content }]);
  }, []);

  const appendTrishMessages = useCallback(async (contents: string[]) => {
    for (const content of contents) {
      setIsThinking(true);
      await new Promise((resolve) => setTimeout(resolve, thinkingDelayMs()));
      setIsThinking(false);
      setMessages((prev) => [...prev, { id: nextId(), role: "trish", content }]);
    }
  }, []);

  const finishPreAuthAndContinue = useCallback(
    async (opts: {
      role: string;
      useCase: string;
      site: string | null;
      name: string;
      agent: string;
      preference: TrishConfigPreference;
    }) => {
      const prompt = buildTrishHirePrompt({
        roleLabel: opts.role,
        useCaseDescription: opts.useCase,
        businessName: opts.name,
        websiteUrl: opts.site,
        agentName: opts.agent,
        configPreference: opts.preference,
        goals: selectedGoals,
        channels: selectedChannels,
        productName,
      });
      persistInitialPrompt(prompt);
      onConfigPreference?.(opts.preference);
      writeSavedHireBuilderOnboardingState({
        step: isSignedIn ? "setup" : "sign_up",
        submittedWebsite: opts.site ?? "",
      });

      await appendTrishMessages([trishBuildingMessage(opts.name, opts.role)]);

      if (!isSignedIn) {
        setStep("sign_up");
        await appendTrishMessages([trishSignUpMessage(opts.name, opts.role)]);
        if (!signUpRequestedRef.current) {
          signUpRequestedRef.current = true;
          onRequestSignUp?.();
        }
        return;
      }

      setStep("complete");
      markHireBuilderOnboardingComplete();
      writeSavedHireBuilderOnboardingState({
        step: "complete",
        submittedWebsite: opts.site ?? "",
      });
      // Flush transcript before Build chat mounts (Preview generate).
      const key = normalizeHireWebsiteSessionKey(opts.site || opts.name || "pending");
      setMessages((prev) => {
        writeHireTrishOnboardingTranscript(key, prev);
        return prev;
      });
      if (!setupStartedRef.current) {
        setupStartedRef.current = true;
        onSetupAgent();
      }
    },
    [
      appendTrishMessages,
      isSignedIn,
      onConfigPreference,
      onRequestSignUp,
      onSetupAgent,
      productName,
      selectedChannels,
      selectedGoals,
    ],
  );

  // After website analysis completes
  useEffect(() => {
    if (!awaitingBootstrapRef.current) return;
    if (isAnalyzing) return;

    if (bootstrapError) {
      awaitingBootstrapRef.current = false;
      postAnalyzeStartedRef.current = false;
      void appendTrishMessages([
        bootstrapError ||
          "I couldn't analyze that website. Please check the URL and try again.",
      ]);
      setStep("website");
      return;
    }

    if (!bootstrapComplete) return;
    if (postAnalyzeStartedRef.current) return;
    postAnalyzeStartedRef.current = true;
    awaitingBootstrapRef.current = false;

    const name = resolvedBusinessName;
    setBusinessName(name);
    void appendTrishMessages([trishGoalsPrompt(name, productName)]).then(() => {
      setStep("goals");
    });
  }, [
    appendTrishMessages,
    bootstrapComplete,
    bootstrapError,
    isAnalyzing,
    productName,
    resolvedBusinessName,
  ]);

  const toggleGoal = useCallback((goal: string) => {
    setSelectedGoals((prev) =>
      prev.includes(goal) ? prev.filter((g) => g !== goal) : [...prev, goal],
    );
  }, []);

  const toggleChannel = useCallback((channel: string) => {
    setSelectedChannels((prev) =>
      prev.includes(channel) ? prev.filter((c) => c !== channel) : [...prev, channel],
    );
  }, []);

  const continueFromGoals = useCallback(async () => {
    if (isBusy || selectedGoals.length === 0) return;
    appendUserMessage(selectedGoals.join(", "));
    await appendTrishMessages([trishChannelsPrompt(productName)]);
    setStep("channels");
  }, [appendTrishMessages, appendUserMessage, isBusy, productName, selectedGoals]);

  const continueFromChannels = useCallback(async () => {
    if (isBusy || selectedChannels.length === 0) return;
    appendUserMessage(selectedChannels.join(", "));
    const roles = recommendTrishAgentRoles({
      vertical,
      recommendedJobTitle: suggestedProfile?.jobTitle,
      goals: selectedGoals,
      channels: selectedChannels,
      productName,
    });
    setRoleRecommendations(roles);
    await appendTrishMessages([
      formatTrishRoleRecommendationsMessage(resolvedBusinessName, roles, productName),
    ]);
    setStep("job_title");
  }, [
    appendTrishMessages,
    appendUserMessage,
    isBusy,
    productName,
    resolvedBusinessName,
    selectedChannels,
    selectedGoals,
    suggestedProfile?.jobTitle,
    vertical,
  ]);

  const applyJobTitle = useCallback(
    async (title: string) => {
      const trimmed = title.trim();
      if (!trimmed || isBusy) return;
      const matched = roleRecommendations.find(
        (r) => r.title.toLowerCase() === trimmed.toLowerCase(),
      );
      const role = matched?.roleLabel ?? resolveRoleLabelFromJobTitle(trimmed);
      const useCase =
        matched?.promptStarter ??
        promptStarterForJobTitle(trimmed, resolvedBusinessName, productName);
      setJobTitle(trimmed);
      setRoleLabel(role);
      setUseCaseDescription(useCase);
      setDraft("");
      appendUserMessage(trimmed);
      await appendTrishMessages([trishAgentProfilePrompt(trimmed, resolvedBusinessName)]);
      setStep("agent_profile");
    },
    [
      appendTrishMessages,
      appendUserMessage,
      isBusy,
      productName,
      resolvedBusinessName,
      roleRecommendations,
    ],
  );

  const applyAgentProfile = useCallback(
    async (name: string, imageUrl: string) => {
      const trimmedName = name.trim();
      if (!trimmedName || isBusy) return;
      const image =
        imageUrl.trim() ||
        suggestedProfile?.profileImageUrl ||
        profileOptions[0]?.imageUrl ||
        "";
      setAgentName(trimmedName);
      setProfileImageUrl(image);
      setDraft("");
      appendUserMessage(trimmedName);
      onAgentProfileSelected?.({
        agentName: trimmedName,
        jobTitle: jobTitle || suggestedProfile?.jobTitle || `${productName} Agent`,
        profileImageUrl: image,
      });
      await appendTrishMessages([
        trishConfigChoicePrompt(resolvedBusinessName, trimmedName),
      ]);
      setStep("config_choice");
    },
    [
      appendTrishMessages,
      appendUserMessage,
      isBusy,
      jobTitle,
      onAgentProfileSelected,
      productName,
      profileOptions,
      resolvedBusinessName,
      suggestedProfile?.jobTitle,
      suggestedProfile?.profileImageUrl,
    ],
  );

  const handleSend = async () => {
    const text = draft.trim();
    if (!text || isBusy) return;

    if (step === "website") {
      const parsedUrl = parseWebsiteFromInput(text);
      setDraft("");
      appendUserMessage(text);

      if (!parsedUrl) {
        await appendTrishMessages([
          "Please enter a valid website URL — for example, https://www.yourbusiness.com",
        ]);
        return;
      }

      setWebsiteUrl(parsedUrl);
      awaitingBootstrapRef.current = true;
      postAnalyzeStartedRef.current = false;
      writeSavedHireBuilderOnboardingState({
        step: "analyzing",
        submittedWebsite: parsedUrl,
      });
      // Spinner below shows "Analyzing…" — do not also append a chat bubble (duplicate).
      setIsThinking(true);
      setStep("analyzing");
      onSubmitWebsite(parsedUrl);
      return;
    }

    if (step === "job_title") {
      await applyJobTitle(text);
      return;
    }

    if (step === "agent_profile") {
      const matched = profileOptions.find(
        (p) => p.name.toLowerCase() === text.toLowerCase(),
      );
      await applyAgentProfile(
        text,
        matched?.imageUrl ||
          suggestedProfile?.profileImageUrl ||
          profileOptions[0]?.imageUrl ||
          "",
      );
    }
  };

  const handleConfigChoice = (preference: TrishConfigPreference) => {
    if (isBusy) return;
    setConfigPreference(preference);
    appendUserMessage(
      preference === "free"
        ? "Generate the free version"
        : `Generate a recommended configuration for ${resolvedBusinessName}`,
    );
    void finishPreAuthAndContinue({
      role: roleLabel || resolveRoleLabelFromJobTitle(jobTitle),
      useCase:
        useCaseDescription ||
        promptStarterForJobTitle(jobTitle, resolvedBusinessName, productName),
      site: websiteUrl,
      name: resolvedBusinessName,
      agent: agentName || suggestedProfile?.agentName || "your agent",
      preference,
    });
  };

  const handleProfileImageUpload = async (file: File) => {
    if (!file.type.startsWith("image/")) {
      toast.error("Please upload an image file.");
      return;
    }
    setIsUploadingImage(true);
    try {
      const response = await fetch(`/api/upload/avatar?filename=${encodeURIComponent(file.name)}`, {
        method: "POST",
        body: file,
      });
      if (!response.ok) throw new Error("Upload failed");
      const result = (await response.json()) as { url?: string };
      if (!result.url) throw new Error("No URL returned");
      setProfileImageUrl(result.url);
      const name = draft.trim() || agentName || suggestedProfile?.agentName || "Custom";
      setDraft(name);
      onAgentProfileSelected?.({
        agentName: name,
        jobTitle: jobTitle || suggestedProfile?.jobTitle || `${productName} Agent`,
        profileImageUrl: result.url,
      });
      toast.success("Profile image uploaded — send a name or tap a suggestion to continue.");
    } catch {
      toast.error("Failed to upload profile image");
    } finally {
      setIsUploadingImage(false);
    }
  };

  // After Sign Up / email verify (same tab or new tab): continue from saved step.
  useEffect(() => {
    if (!isSignedIn || setupStartedRef.current) return;
    if (step !== "sign_up") return;
    if (!bootstrapComplete) return;

    setupStartedRef.current = true;
    const preference = configPreference ?? "recommended";
    onConfigPreference?.(preference);
    markHireBuilderOnboardingComplete();
    writeSavedHireBuilderOnboardingState({
      step: "complete",
      submittedWebsite: websiteUrl ?? "",
    });
    setStep("complete");
    void appendTrishMessages([
      `Welcome back! I'm finishing your ${resolvedBusinessName} ${roleLabel || "Inbound GPT"} agent preview now.`,
    ]).then(() => {
      setMessages((prev) => {
        persistTranscript(prev, "complete");
        return prev;
      });
      onSetupAgent();
    });
  }, [
    appendTrishMessages,
    bootstrapComplete,
    configPreference,
    isSignedIn,
    onConfigPreference,
    onSetupAgent,
    persistTranscript,
    resolvedBusinessName,
    roleLabel,
    step,
    websiteUrl,
  ]);

  const showTextInput =
    step === "website" || step === "job_title" || step === "agent_profile";
  const inputPlaceholder =
    step === "website"
      ? "What's your organization's website?"
      : step === "job_title"
        ? "Job title, e.g. Sales Engineer"
        : "Agent name, e.g. Evelyn";

  const toggleChipClass = (selected: boolean) =>
    cn(
      "rounded-full border px-3 py-1.5 text-sm font-medium transition-colors",
      selected
        ? "border-primary bg-primary/10 text-primary"
        : "border-slate-200 bg-white text-slate-700 hover:border-primary/40 hover:bg-primary/5",
    );

  return (
    <div className={cn("flex h-full min-h-0 flex-col", className)}>
      <div className="flex min-h-0 flex-1 flex-col">
        <div
          ref={scrollRef}
          className="min-h-0 flex-1 space-y-3 overflow-y-auto rounded-lg border border-slate-200 bg-slate-50/50 p-4"
        >
          {messages.map((message) =>
            message.role === "user" ? (
              <div
                key={message.id}
                className="ml-auto max-w-[92%] rounded-lg bg-primary px-3 py-2 text-sm text-primary-foreground"
              >
                <p className="whitespace-pre-wrap leading-relaxed">{message.content}</p>
              </div>
            ) : (
              <div key={message.id} className="flex max-w-[92%] gap-2">
                <ConsultantAvatar
                  size={32}
                  className="mt-0.5"
                  src={consultantAvatarSrc}
                  alt={consultantName}
                />
                <div className="rounded-lg bg-white px-3 py-2 text-sm text-slate-800 shadow-sm ring-1 ring-slate-200">
                  <p className="whitespace-pre-wrap leading-relaxed">{message.content}</p>
                </div>
              </div>
            ),
          )}

          {isBusy ? (
            <div className="flex max-w-[92%] items-center gap-2">
              <ConsultantAvatar
                size={32}
                className="mt-0.5 opacity-80"
                src={consultantAvatarSrc}
                alt={consultantName}
              />
              <div className="flex items-center gap-2 rounded-lg bg-white px-3 py-2 text-sm text-slate-500 shadow-sm ring-1 ring-slate-200">
                <Loader2 className="h-4 w-4 animate-spin" />
                {isAnalyzing || awaitingBootstrapRef.current
                  ? "Analyzing your website…"
                  : "Thinking…"}
              </div>
            </div>
          ) : null}

          {step === "goals" && !isBusy ? (
            <div className="space-y-3 pt-1">
              <div className="flex flex-wrap gap-2">
                {resolvedGoalOptions.map((goal) => (
                  <button
                    key={goal}
                    type="button"
                    onClick={() => toggleGoal(goal)}
                    className={toggleChipClass(selectedGoals.includes(goal))}
                  >
                    {goal}
                  </button>
                ))}
              </div>
              <Button
                type="button"
                size="sm"
                disabled={selectedGoals.length === 0}
                onClick={() => void continueFromGoals()}
              >
                Continue
              </Button>
            </div>
          ) : null}

          {step === "channels" && !isBusy ? (
            <div className="space-y-3 pt-1">
              <div className="flex flex-wrap gap-2">
                {TRISH_CHANNEL_OPTIONS.map((channel) => (
                  <button
                    key={channel}
                    type="button"
                    onClick={() => toggleChannel(channel)}
                    className={toggleChipClass(selectedChannels.includes(channel))}
                  >
                    {channel}
                  </button>
                ))}
              </div>
              <Button
                type="button"
                size="sm"
                disabled={selectedChannels.length === 0}
                onClick={() => void continueFromChannels()}
              >
                Continue
              </Button>
            </div>
          ) : null}

          {step === "job_title" && !isBusy ? (
            <div className="flex flex-wrap gap-2 pt-1">
              {jobTitleOptions.map((title) => (
                <button
                  key={title}
                  type="button"
                  onClick={() => void applyJobTitle(title)}
                  className="rounded-full border border-slate-200 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 transition-colors hover:border-primary/40 hover:bg-primary/5"
                >
                  {title}
                </button>
              ))}
            </div>
          ) : null}

          {step === "agent_profile" && !isBusy ? (
            <div className="flex flex-wrap gap-2 pt-1">
              {profileOptions.map((profile) => (
                <button
                  key={`${profile.name}-${profile.imageUrl}`}
                  type="button"
                  onClick={() => void applyAgentProfile(profile.name, profile.imageUrl)}
                  className="flex items-center gap-2 rounded-full border border-slate-200 bg-white py-1.5 pl-1.5 pr-3 text-sm font-medium text-slate-700 transition-colors hover:border-primary/40 hover:bg-primary/5"
                >
                  {/* eslint-disable-next-line @next/next/no-img-element */}
                  <img
                    src={profile.imageUrl}
                    alt=""
                    className="h-7 w-7 rounded-full object-cover"
                  />
                  {profile.name}
                </button>
              ))}
            </div>
          ) : null}

          {step === "config_choice" && !isBusy ? (
            <div className="flex flex-col gap-2 pt-1 sm:flex-row sm:flex-wrap">
              <button
                type="button"
                onClick={() => handleConfigChoice("free")}
                className="rounded-lg border border-slate-200 bg-white px-4 py-2.5 text-left text-sm font-medium text-slate-700 transition-colors hover:border-primary/40 hover:bg-primary/5"
              >
                Free version — Live Chat, 1 location, 1 team member
              </button>
              <button
                type="button"
                onClick={() => handleConfigChoice("recommended")}
                className="rounded-lg border border-primary/30 bg-primary/5 px-4 py-2.5 text-left text-sm font-medium text-slate-800 transition-colors hover:border-primary/50 hover:bg-primary/10"
              >
                Recommended configuration for {resolvedBusinessName}
              </button>
            </div>
          ) : null}

          {step === "sign_up" && !isBusy && !isSignedIn ? (
            <p className="text-xs text-muted-foreground">
              Use the Sign Up form in the Preview tab to continue.
            </p>
          ) : null}

          {step === "complete" && !isBusy ? (
            <div className="flex items-center gap-2 text-sm text-slate-600">
              <Loader2 className="h-4 w-4 animate-spin text-primary" />
              Setting up your agent preview…
            </div>
          ) : null}
        </div>

        {showTextInput ? (
          <div className="mt-4 shrink-0">
            <form
              onSubmit={(e) => {
                e.preventDefault();
                void handleSend();
              }}
              className="flex items-center gap-2"
            >
              {step === "agent_profile" ? (
                <>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      e.target.value = "";
                      if (file) void handleProfileImageUpload(file);
                    }}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    disabled={isBusy}
                    title="Upload profile image"
                    aria-label="Upload profile image"
                    onClick={() => fileInputRef.current?.click()}
                    className="shrink-0"
                  >
                    {isUploadingImage ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Paperclip className="h-4 w-4" />
                    )}
                  </Button>
                  {profileImageUrl ? (
                    // eslint-disable-next-line @next/next/no-img-element
                    <img
                      src={profileImageUrl}
                      alt=""
                      className="h-9 w-9 shrink-0 rounded-full object-cover ring-1 ring-slate-200"
                    />
                  ) : null}
                </>
              ) : null}
              <Input
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                placeholder={inputPlaceholder}
                disabled={isBusy}
                className="flex-1"
              />
              <Button type="submit" disabled={isBusy || !draft.trim()}>
                Send
              </Button>
            </form>
          </div>
        ) : null}
      </div>
    </div>
  );
}
